import { useCallback, useMemo } from 'react'

import { useSession } from '../session'

interface PriceFormatterOptions {
  decimals?: boolean
}

export const usePriceFormatter = ({ decimals }: PriceFormatterOptions = {}) => {
  const { currency, locale } = useSession()

  return useCallback(
    (price: number) =>
      Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency.code,
        minimumFractionDigits: 2,
      }).format(price),
    [currency.code, locale]
  )
}

const useUSDPriceFormatter = () => {
  return useCallback((price: number) => {
    // Divide the price by 100 to get the correct format with two decimal places
    const adjustedPrice = price / 100

    const roundedPrice = Math.round(adjustedPrice)

    // Create a formatter for the 'de-DE' locale (German) which uses period for thousand separator and comma for decimal
    const formatter = new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })

    // Format the price
    return `USD ${formatter.format(roundedPrice).replace('$', '').trim()}`
  }, [])
}

export const useFormattedPrice = (price: number) => {
  const formatter = usePriceFormatter()

  return useMemo(() => formatter(price), [formatter, price])
}

export const useFormattedUSDPrice = (price: number) => {
  const formatterUSD = useUSDPriceFormatter()

  return useMemo(() => formatterUSD(price), [formatterUSD, price])
}
